import type { ApiLot } from '@/types/inventory'

// Receipts of one vendor lot, gathered.
//
// Since the server started numbering receipts (`LOT-26-0001-1`, `-2`, `-3`), a
// material can hold several rows that are all one supplier lot. Listing them as
// peers buries the only relationship that matters: a VENDOR LOT is what a recall
// names and what an expiry applies to, and a RECEIPT is one delivery of it. So
// the table groups by the first and expands to the second.
//
// Pure and dependency-free, because this repo's vitest config is
// `environment: 'node'` and only collects `src` tests ending in `.test.ts` —
// logic left inside the .tsx would silently have no coverage at all.

/** `LOT-<yy>-<nnnn>-<n>` — see cgt-backend `inventory/materials/lot-numbering.ts`. */
const LOT_NUMBER_RE = /^LOT-(\d{2})-(\d{4,})-(\d+)$/
/** Rows written before receipts were numbered. Read as the first receipt. */
const LEGACY_LOT_NUMBER_RE = /^LOT-(\d{2})-(\d{4,})$/

function parse(lotNumber: string): { yy: string; base: number; suffix: number } | null {
  const m = LOT_NUMBER_RE.exec(lotNumber)
  if (m) return { yy: m[1], base: Number(m[2]), suffix: Number(m[3]) }
  const legacy = LEGACY_LOT_NUMBER_RE.exec(lotNumber)
  if (legacy) return { yy: legacy[1], base: Number(legacy[2]), suffix: 1 }
  return null
}

/**
 * Lot numbers, oldest first — year, then vendor lot, then receipt, each
 * compared as the NUMBER it is.
 *
 * The receipt suffix is deliberately not zero-padded, so as text `-10` sorts
 * above `-2`. Every ordering in the lot table goes through here for that one
 * reason. Numbers from another scheme (kit-built units, seeded stock) have no
 * such structure and fall back to text, which at least orders deterministically.
 *
 * Mirrors `compareLotNumbers` in cgt-backend. The duplication is the same
 * duplicated-by-design the domain dictionary already carries across the wire —
 * if the format changes, both sides move together.
 */
export function compareLotNumbers(a: string, b: string): number {
  const pa = parse(a)
  const pb = parse(b)
  if (!pa || !pb) return a.localeCompare(b)
  return pa.yy.localeCompare(pb.yy) || pa.base - pb.base || pa.suffix - pb.suffix
}

/** On hand, spoken for, and still committable — totalled over a set of lots. */
export interface LotStockTotals {
  /** Total on the shelf, held stock included. */
  quantity: number
  /** Held by scheduled runs and in-flight process steps. */
  inUse: number
  /** `quantity` minus `inUse` — what a new run can still take. */
  available: number
}

/**
 * Total a list of lots. This is the summary strip above the lot table.
 *
 * It takes whatever list it is handed rather than a material, because the
 * header totals the lots ON SCREEN: filter to Expired and the summary has to
 * describe the expired stock, which is the question that filter was set to ask.
 *
 * Same `availableQuantity ?? quantity` fallback as `groupLotsByVendorLot` —
 * the field is server-computed but optional on the wire, and without it a
 * material of undecorated rows reads as nothing free.
 */
export function totalLotStock(lots: ApiLot[]): LotStockTotals {
  return lots.reduce<LotStockTotals>(
    (acc, l) => ({
      quantity: acc.quantity + (l.quantity ?? 0),
      inUse: acc.inUse + (l.reservedQuantity ?? 0),
      available: acc.available + (l.availableQuantity ?? l.quantity ?? 0),
    }),
    { quantity: 0, inUse: 0, available: 0 },
  )
}

export interface LotFamily {
  /** Stable React key; distinct for the unnumbered bucket. */
  key: string
  /** The supplier's number, or null for receipts that carry none. */
  vendorLotNumber: string | null
  /** Every receipt in the family, soonest-expiring first. */
  receipts: ApiLot[]
  receiptCount: number
  /** On hand across the family. */
  quantity: number
  /** Spoken for and not yet used, across the family. */
  reserved: number
  /** Actually used, across the family. */
  inUse: number
  /** Still committable — the figure that decides whether a run can be planned. */
  available: number
  /** Soonest expiry among the receipts, or null when none is dated. */
  earliestExpiry: string | null
}

/** Sortable expiry, undated LAST: no clock means it cannot be the soonest to go. */
function expiryRank(iso: string | null | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

function byExpiryThenNumber(a: ApiLot, b: ApiLot): number {
  return (
    expiryRank(a.expiryDate) - expiryRank(b.expiryDate) ||
    compareLotNumbers(a.lotNumber, b.lotNumber)
  )
}

/**
 * Group lots into vendor-lot families, soonest-expiring family first.
 *
 * Receipts carrying NO vendor number are collected under a `null` key purely so
 * they keep a stable place in the ordering. They are NOT a family — two
 * containers that happen to lack a supplier number have nothing in common — so
 * the table lists them as plain rows with no heading over them. Nothing should
 * present that group's totals as the stock of a single lot.
 */
export function groupLotsByVendorLot(lots: ApiLot[]): LotFamily[] {
  const byVendor = new Map<string | null, ApiLot[]>()
  for (const lot of lots) {
    const key = lot.vendorLotNumber?.trim() || null
    const bucket = byVendor.get(key)
    if (bucket) bucket.push(lot)
    else byVendor.set(key, [lot])
  }

  const families: LotFamily[] = []
  for (const [vendorLotNumber, receipts] of byVendor) {
    const ordered = [...receipts].sort(byExpiryThenNumber)
    const dated = ordered.filter((l) => l.expiryDate)
    families.push({
      key: vendorLotNumber === null ? ' no-vendor-lot' : `v:${vendorLotNumber}`,
      vendorLotNumber,
      receipts: ordered,
      receiptCount: ordered.length,
      quantity: ordered.reduce((s, l) => s + (l.quantity ?? 0), 0),
      // `heldQuantity` is the open-hold figure the Reserved column shows.
      // Falls back to `reservedQuantity` for a row that predates it, so a family
      // never reads as unreserved just because the server is older.
      reserved: ordered.reduce((s, l) => s + (l.heldQuantity ?? l.reservedQuantity ?? 0), 0),
      inUse: ordered.reduce((s, l) => s + (l.consumedQuantity ?? 0), 0),
      // `availableQuantity` is server-computed but optional on the wire; without
      // the fallback a family of undecorated rows would read as nothing free.
      available: ordered.reduce((s, l) => s + (l.availableQuantity ?? l.quantity ?? 0), 0),
      earliestExpiry: dated.length > 0 ? (dated[0].expiryDate ?? null) : null,
    })
  }

  return families.sort(
    (a, b) =>
      expiryRank(a.earliestExpiry) - expiryRank(b.earliestExpiry) ||
      compareLotNumbers(a.receipts[0].lotNumber, b.receipts[0].lotNumber),
  )
}

/**
 * One gate status for a group of receipts, or null when none of them is gated.
 *
 * Same precedence the backend uses to roll lots up to their material —
 * available > in_process > quarantine, with an ungated receipt counting as
 * available (it renders that way in `LotGateCell`, and the process-step
 * allocator already treats it that way too) — so a family heading and the
 * material row above it cannot tell different stories about the same stock.
 * The heading answers "can I use any of this vendor lot?", which is the
 * question someone scanning a collapsed family is actually asking.
 */
export function familyGateStatus(receipts: ApiLot[]): string | null {
  const gated = receipts
    .map((r) => r.gateStatus)
    .filter((s): s is NonNullable<ApiLot['gateStatus']> => !!s)
  // No gated receipt at all — nothing to say, same as before. Distinct from
  // SOME gated and SOME ungated, handled just below.
  if (gated.length === 0) return null
  const hasUngatedReceipt = receipts.length > gated.length
  if (gated.includes('available') || hasUngatedReceipt) return 'available'
  if (gated.includes('in_process')) return 'in_process'
  return 'quarantine'
}
